import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  ArrowLeft, Building2, Upload, Save, CreditCard, FileText,
  Banknote, Image, Plus, X, Check, Trash2, MessageSquare, Phone, Key, TestTube, RefreshCw
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export default function CompanySettingsPage({ user }) {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  const [settings, setSettings] = useState({
    logo_url: null,
    payment_methods: ['Espèces', 'Carte bancaire', 'Virement'],
    bank_details: {
      bank_name: '',
      iban: '',
      bic: ''
    },
    cgv_custom: ''
  });
  
  const [companyInfo, setCompanyInfo] = useState(null);
  const [newPaymentMethod, setNewPaymentMethod] = useState('');
  
  // SMS Settings state
  const [smsSettings, setSmsSettings] = useState({
    twilio_account_sid: '',
    twilio_auth_token: '',
    twilio_verify_service: '',
    twilio_phone_number: '',
    is_configured: false,
    source: 'default'
  });
  const [showSmsTokens, setShowSmsTokens] = useState(false);
  const [savingSms, setSavingSms] = useState(false);
  const [testingSms, setTestingSms] = useState(false);
  const [newSmsSettings, setNewSmsSettings] = useState({
    twilio_account_sid: '',
    twilio_auth_token: '',
    twilio_verify_service: '',
    twilio_phone_number: ''
  });
  
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchSettings();
    fetchCompanyInfo();
    fetchSmsSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/company-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings({
        logo_url: response.data.logo_url || null,
        payment_methods: response.data.payment_methods || ['Espèces', 'Carte bancaire', 'Virement'],
        bank_details: response.data.bank_details || { bank_name: '', iban: '', bic: '' },
        cgv_custom: response.data.cgv_custom || ''
      });
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanyInfo = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/company-info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanyInfo(response.data);
    } catch (error) {
      console.error('Error fetching company info:', error);
    }
  };

  const fetchSmsSettings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/sms-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSmsSettings(response.data);
    } catch (error) {
      console.error('Error fetching SMS settings:', error);
    }
  };

  const handleSaveSmsSettings = async () => {
    // Vérifier qu'au moins un champ est rempli
    if (!newSmsSettings.twilio_account_sid && !newSmsSettings.twilio_auth_token) {
      toast.error('Veuillez remplir au moins le SID et le Token');
      return;
    }
    
    setSavingSms(true);
    try {
      await axios.put(`${BACKEND_URL}/api/sms-settings`, newSmsSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Paramètres SMS enregistrés ! Redéployez pour appliquer.');
      fetchSmsSettings();
      setShowSmsTokens(false);
      setNewSmsSettings({
        twilio_account_sid: '',
        twilio_auth_token: '',
        twilio_verify_service: '',
        twilio_phone_number: ''
      });
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement des paramètres SMS');
    } finally {
      setSavingSms(false);
    }
  };

  const handleTestSms = async () => {
    setTestingSms(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/sms-settings/test`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.error || 'Échec du test');
      }
    } catch (error) {
      toast.error('Erreur lors du test SMS');
    } finally {
      setTestingSms(false);
    }
  };

  const handleResetSmsSettings = async () => {
    if (!confirm('Voulez-vous vraiment réinitialiser les paramètres SMS aux valeurs par défaut ?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/sms-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Paramètres SMS réinitialisés');
      fetchSmsSettings();
    } catch (error) {
      toast.error('Erreur lors de la réinitialisation');
    }
  };

  const handleUploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Veuillez sélectionner une image');
      return;
    }
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/company-settings/logo`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setSettings({ ...settings, logo_url: response.data.logo_url });
      toast.success('Logo mis à jour');
    } catch (error) {
      toast.error('Erreur lors de l\'upload du logo');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteLogo = async () => {
    if (!settings.logo_url) return;
    
    if (!confirm('Voulez-vous vraiment supprimer le logo ?')) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/company-settings/logo`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings({ ...settings, logo_url: null });
      toast.success('Logo supprimé');
    } catch (error) {
      toast.error('Erreur lors de la suppression du logo');
    } finally {
      setDeleting(false);
    }
  };

  const handleAddPaymentMethod = () => {
    if (newPaymentMethod.trim() && !settings.payment_methods.includes(newPaymentMethod.trim())) {
      setSettings({
        ...settings,
        payment_methods: [...settings.payment_methods, newPaymentMethod.trim()]
      });
      setNewPaymentMethod('');
    }
  };

  const handleRemovePaymentMethod = (method) => {
    setSettings({
      ...settings,
      payment_methods: settings.payment_methods.filter(m => m !== method)
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/company-settings`, {
        payment_methods: settings.payment_methods,
        bank_name: settings.bank_details.bank_name,
        iban: settings.bank_details.iban,
        bic: settings.bank_details.bic,
        cgv_custom: settings.cgv_custom
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Paramètres enregistrés');
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button onClick={() => navigate('/invoices')} variant="outline" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
            <h1 className="font-heading text-xl font-bold text-slate-900">
              Paramètres de l'entreprise
            </h1>
          </div>
          <Button onClick={handleSave} disabled={saving} className="bg-green-600 hover:bg-green-700">
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-4 md:p-8 space-y-6">
        
        {/* Logo Section */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Image className="w-5 h-5" />
            Logo de l'entreprise
          </h2>
          
          <div className="flex items-center gap-6">
            <div className="w-32 h-32 border-2 border-dashed border-slate-300 rounded-xl flex items-center justify-center bg-slate-50 overflow-hidden">
              {settings.logo_url ? (
                <img 
                  src={`${BACKEND_URL}${settings.logo_url}`} 
                  alt="Logo" 
                  className="w-full h-full object-contain"
                />
              ) : (
                <Building2 className="w-12 h-12 text-slate-300" />
              )}
            </div>
            
            <div>
              <p className="text-sm text-slate-600 mb-3">
                Uploadez le logo de votre entreprise. Il apparaîtra sur vos factures et devis.
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleUploadLogo}
                className="hidden"
              />
              <div className="flex gap-2">
                <Button 
                  onClick={() => fileInputRef.current?.click()}
                  variant="outline"
                  disabled={uploading}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {uploading ? 'Upload...' : 'Choisir une image'}
                </Button>
                {settings.logo_url && (
                  <Button 
                    onClick={handleDeleteLogo}
                    variant="outline"
                    disabled={deleting}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    {deleting ? 'Suppression...' : 'Supprimer'}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Payment Methods */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <CreditCard className="w-5 h-5" />
            Modes de paiement acceptés
          </h2>
          
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              {settings.payment_methods.map((method, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-2 px-3 py-2 bg-slate-100 rounded-lg"
                >
                  <Check className="w-4 h-4 text-green-600" />
                  <span className="text-sm font-medium">{method}</span>
                  <button
                    onClick={() => handleRemovePaymentMethod(method)}
                    className="text-slate-400 hover:text-red-500"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            
            <div className="flex gap-2">
              <Input
                value={newPaymentMethod}
                onChange={(e) => setNewPaymentMethod(e.target.value)}
                placeholder="Ajouter un mode de paiement..."
                onKeyPress={(e) => e.key === 'Enter' && handleAddPaymentMethod()}
              />
              <Button onClick={handleAddPaymentMethod} variant="outline">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Bank Details */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Banknote className="w-5 h-5" />
            Coordonnées bancaires (pour virement)
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Nom de la banque</Label>
              <Input
                value={settings.bank_details.bank_name}
                onChange={(e) => setSettings({
                  ...settings,
                  bank_details: { ...settings.bank_details, bank_name: e.target.value }
                })}
                placeholder="Ex: Crédit Agricole"
              />
            </div>
            <div>
              <Label>IBAN</Label>
              <Input
                value={settings.bank_details.iban}
                onChange={(e) => setSettings({
                  ...settings,
                  bank_details: { ...settings.bank_details, iban: e.target.value }
                })}
                placeholder="FR76 XXXX XXXX XXXX"
              />
            </div>
            <div>
              <Label>BIC</Label>
              <Input
                value={settings.bank_details.bic}
                onChange={(e) => setSettings({
                  ...settings,
                  bank_details: { ...settings.bank_details, bic: e.target.value }
                })}
                placeholder="AGRIFRPP"
              />
            </div>
          </div>
        </div>

        {/* CGV Custom */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Conditions générales personnalisées
          </h2>
          
          <p className="text-sm text-slate-600 mb-4">
            Ajoutez des conditions générales supplémentaires qui seront affichées sur vos factures.
            Les conditions par défaut (garanties, retard de paiement, etc.) seront automatiquement incluses.
          </p>
          
          <textarea
            value={settings.cgv_custom}
            onChange={(e) => setSettings({ ...settings, cgv_custom: e.target.value })}
            placeholder="Conditions supplémentaires..."
            className="w-full border border-slate-200 rounded-lg px-4 py-3 min-h-[150px]"
          />
        </div>

        {/* Default CGV Preview */}
        {companyInfo?.cgv && (
          <div className="bg-slate-50 rounded-xl border border-slate-200 p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">
              Conditions générales par défaut (incluses automatiquement)
            </h2>
            
            <div className="space-y-4 text-sm text-slate-600">
              <div>
                <h3 className="font-medium text-slate-800">Paiement</h3>
                <p>{companyInfo.cgv.paiement}</p>
              </div>
              
              <div>
                <h3 className="font-medium text-slate-800">Retard de paiement</h3>
                <p>{companyInfo.cgv.retard_paiement}</p>
              </div>
              
              <div>
                <h3 className="font-medium text-slate-800">Garanties</h3>
                <ul className="list-disc list-inside space-y-1">
                  {companyInfo.cgv.garanties?.map((g, i) => (
                    <li key={i}>{g}</li>
                  ))}
                </ul>
              </div>
              
              <div>
                <h3 className="font-medium text-slate-800">Données personnelles</h3>
                <p>{companyInfo.cgv.donnees_personnelles}</p>
              </div>
              
              <div>
                <h3 className="font-medium text-slate-800">Service après-vente</h3>
                <p>{companyInfo.cgv.sav}</p>
              </div>
            </div>
          </div>
        )}

        {/* SMS Settings Section */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-green-600" />
            Paramètres SMS (Twilio)
          </h2>
          
          <p className="text-sm text-slate-600 mb-4">
            Configurez vos credentials Twilio pour l'envoi de SMS (notifications clients, 2FA).
            Ces paramètres seront appliqués après redéploiement.
          </p>
          
          {/* Current Status */}
          <div className={`p-4 rounded-lg mb-6 ${smsSettings.is_configured ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`font-medium ${smsSettings.is_configured ? 'text-green-800' : 'text-amber-800'}`}>
                  {smsSettings.is_configured ? '✓ SMS configuré' : '⚠ SMS non configuré'}
                </p>
                <p className="text-sm text-slate-600 mt-1">
                  Source: {smsSettings.source === 'custom' ? 'Personnalisé' : 'Configuration par défaut'}
                </p>
              </div>
              <div className="flex gap-2">
                <Button 
                  onClick={handleTestSms} 
                  disabled={testingSms || !smsSettings.is_configured}
                  variant="outline"
                  size="sm"
                >
                  {testingSms ? <RefreshCw className="w-4 h-4 animate-spin" /> : <TestTube className="w-4 h-4" />}
                  <span className="ml-2">Tester</span>
                </Button>
              </div>
            </div>
          </div>
          
          {/* Current Values (masked) */}
          <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
            <div>
              <span className="text-slate-500">Account SID:</span>
              <span className="ml-2 font-mono">{smsSettings.twilio_account_sid || 'Non configuré'}</span>
            </div>
            <div>
              <span className="text-slate-500">Auth Token:</span>
              <span className="ml-2 font-mono">{smsSettings.twilio_auth_token || 'Non configuré'}</span>
            </div>
            <div>
              <span className="text-slate-500">Verify Service:</span>
              <span className="ml-2 font-mono">{smsSettings.twilio_verify_service || 'Non configuré'}</span>
            </div>
            <div>
              <span className="text-slate-500">Numéro:</span>
              <span className="ml-2 font-mono">{smsSettings.twilio_phone_number || 'Non configuré'}</span>
            </div>
          </div>
          
          {/* Toggle Edit Form */}
          <div className="border-t border-slate-200 pt-4">
            {!showSmsTokens ? (
              <Button onClick={() => setShowSmsTokens(true)} variant="outline" className="w-full">
                <Key className="w-4 h-4 mr-2" />
                Modifier les credentials Twilio
              </Button>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="twilio_sid">Account SID</Label>
                    <Input
                      id="twilio_sid"
                      value={newSmsSettings.twilio_account_sid}
                      onChange={(e) => setNewSmsSettings({ ...newSmsSettings, twilio_account_sid: e.target.value })}
                      placeholder="ACxxxxxxxxxxxxx"
                      className="font-mono text-sm"
                    />
                  </div>
                  <div>
                    <Label htmlFor="twilio_token">Auth Token</Label>
                    <Input
                      id="twilio_token"
                      type="password"
                      value={newSmsSettings.twilio_auth_token}
                      onChange={(e) => setNewSmsSettings({ ...newSmsSettings, twilio_auth_token: e.target.value })}
                      placeholder="Nouveau token..."
                      className="font-mono text-sm"
                    />
                  </div>
                  <div>
                    <Label htmlFor="twilio_verify">Verify Service ID</Label>
                    <Input
                      id="twilio_verify"
                      value={newSmsSettings.twilio_verify_service}
                      onChange={(e) => setNewSmsSettings({ ...newSmsSettings, twilio_verify_service: e.target.value })}
                      placeholder="VAxxxxxxxxxxxxx"
                      className="font-mono text-sm"
                    />
                  </div>
                  <div>
                    <Label htmlFor="twilio_phone">Numéro d'envoi</Label>
                    <Input
                      id="twilio_phone"
                      value={newSmsSettings.twilio_phone_number}
                      onChange={(e) => setNewSmsSettings({ ...newSmsSettings, twilio_phone_number: e.target.value })}
                      placeholder="+1xxxxxxxxxx"
                      className="font-mono text-sm"
                    />
                  </div>
                </div>
                
                <div className="flex gap-2 justify-end">
                  <Button onClick={() => setShowSmsTokens(false)} variant="outline">
                    Annuler
                  </Button>
                  {smsSettings.source === 'custom' && (
                    <Button onClick={handleResetSmsSettings} variant="outline" className="text-red-600 border-red-200 hover:bg-red-50">
                      <Trash2 className="w-4 h-4 mr-2" />
                      Réinitialiser
                    </Button>
                  )}
                  <Button onClick={handleSaveSmsSettings} disabled={savingSms} className="bg-green-600 hover:bg-green-700">
                    {savingSms ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                    Enregistrer
                  </Button>
                </div>
                
                <p className="text-xs text-slate-500 mt-2">
                  💡 Trouvez vos credentials sur <a href="https://console.twilio.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">console.twilio.com</a>
                </p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
